<template>
  <SoWidget data-testid="draw-detail-winners">
    <template #title>
      <div class="d-flex align-center justify-space-between">
        <span>Winners</span>
        <div class="d-flex align-center">
          <SoExportCsvButton
            v-if="winners.length && !calculating"
            class="mr-2"
            :data="csvData"
            :labels="headers"
            :file-name="`draw-${drawId}-winners.csv`"
            @export="exportCsv"
          />
          <span
            v-if="winners.length && !hasUnsavedChanges && !calculating"
            class="text-caption text-medium-emphasis"
            data-testid="winners-summary"
          >
            {{ summaryText }}
          </span>
          <template v-if="hasUnsavedChanges && !calculating">
            <SoButton
              title="Cancel"
              class="mr-2"
              data-testid="winners-cancel-btn"
              @click="resetChanges"
            />
            <SoButton
              title="Review & Save"
              type="success"
              data-testid="winners-save-btn"
              @click="showReviewModal = true"
            />
          </template>
        </div>
      </div>
    </template>

    <SoAlert
      v-if="!loading && !calculating && !error && missingCountyCount"
      type="warning"
      :title="missingCountyTitle"
      text="Correct them before notifying, otherwise they'll show a dash on the winners' wall."
      class="mb-4"
      data-testid="winners-no-county"
    />

    <SoAlert
      v-if="!loading && !calculating && !error && noDirectDebitCount"
      type="warning"
      :title="noDirectDebitTitle"
      text="Check them before notifying. They're flagged in the Status column."
      class="mb-4"
      data-testid="winners-no-direct-debit"
    />

    <div
      v-if="calculating"
      class="d-flex align-center text-medium-emphasis"
      data-testid="winners-calculating"
    >
      <SoProgress indeterminate :size="20" class="mr-3" />
      Winners are being selected. This can take a few hours — check back later.
    </div>

    <SoAlert
      v-else-if="error"
      type="error"
      title="Failed to load winners"
      data-testid="winners-error"
    />

    <div
      v-else-if="!loading && !winners.length"
      class="text-medium-emphasis"
      data-testid="winners-empty-state"
    >
      No winners to show yet. Winners appear here once the draw has closed and
      the selection has run.
    </div>

    <template v-else>
      <div
        class="text-caption text-medium-emphasis mb-2"
        data-testid="winners-direct-debit-note"
      >
        Direct Debit status shows the account's current state, not its state at
        the time of the draw.
      </div>

      <SoDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        searchable
        search-placeholder="Search winners"
        data-testid="draw-detail-winners-table"
      >
        <template #[`item.accountNumber`]="{ internalItem }">
          <router-link
            v-if="internalItem.raw.accountNumber"
            :to="{
              name: RouteNames.CustomerOverview,
              params: { accountNumber: internalItem.raw.accountNumber },
            }"
            target="_blank"
            class="text-primary"
            data-testid="winner-account-link"
          >
            {{ internalItem.raw.accountNumber }}
          </router-link>
          <span v-else>—</span>
        </template>

        <template #[`item.county`]="{ internalItem }">
          <SoTextField
            :model-value="internalItem.raw.county"
            hide-details
            class="winner-county-field"
            data-testid="winner-county-field"
            @update:model-value="
              (value: string) => setCounty(internalItem.raw.id, value)
            "
          />
        </template>

        <template #[`item.anonymous`]="{ internalItem }">
          <SoToggle
            :model-value="internalItem.raw.anonymous"
            data-testid="winner-anonymous-toggle"
            @update:model-value="
              (value: boolean) => setAnonymous(internalItem.raw.id, value)
            "
          />
        </template>

        <template #[`item.status`]="{ internalItem }">
          <div class="d-flex flex-wrap align-center ga-1">
            <SoChip
              :type="internalItem.raw.statusChipType"
              :text="internalItem.raw.statusLabel"
              size="small"
              data-testid="winner-status-chip"
            />
            <SoChip
              v-for="flag in internalItem.raw.flags"
              :key="flag"
              :text="flag"
              size="small"
              data-testid="winner-flag-chip"
            />
          </div>
          <div
            v-if="internalItem.raw.rewardProcessError"
            class="text-caption text-error mt-1"
            data-testid="winner-reward-error"
          >
            {{ internalItem.raw.rewardProcessError }}
          </div>
        </template>

        <template #itemActions="{ internalItem }">
          <div class="d-flex justify-end">
            <SoButton
              v-if="internalItem.raw.canReject"
              title="Reject"
              type="table-action"
              :disabled="hasUnsavedChanges"
              data-testid="winner-reject-btn"
              @click="openRejectModal(internalItem.raw.id)"
            />
          </div>
        </template>
      </SoDataTable>
    </template>

    <OpsWinnerReviewModal
      v-model="showReviewModal"
      :draw-id="drawId"
      :changes="changes"
      @saved="onSaved"
    />
    <OpsWinnerRejectModal
      v-model="showRejectModal"
      :draw-id="drawId"
      :winner="rejectWinner"
      @saved="onSaved"
    />
  </SoWidget>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { cloneDeep } from "lodash-es"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"
  import SoProgress from "@/components/SoProgress.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoExportCsvButton, {
    SoExportCsvButtonData,
  } from "@/components/dataTable/SoExportCsvButton.vue"
  import OpsWinnerReviewModal from "./OpsWinnerReviewModal.vue"
  import OpsWinnerRejectModal from "./OpsWinnerRejectModal.vue"
  import { SoDataTableHeader } from "@/types/components/SoDataTable"
  import { SoChipType } from "@/types/components/SoChip"
  import { PrizeDrawEntryWinnerStatus } from "@/api/backofficeRest/generated/model"
  import {
    IDrawWinner,
    IWinnerChange,
  } from "@/models/operations/draw/drawWinner.types"
  import { IDrawReward } from "@/models/operations/draw/drawReward.types"
  import { RouteNames } from "@/types/router"
  import { formatDateSlashed } from "@/helpers/dateHelper"

  // Only REJECTED is colour-coded (it must read as distinct — AC). SELECTED and
  // APPROVED are both "standing winner" states and share the neutral grey DEFAULT
  // chip — the brand success/primary/warning tonal chips all fail WCAG contrast as
  // text on white (success = green400 #00D9B0 ≈ 1.8:1), so there is no accessible
  // green token; only ERROR (red800 #AF001F) and DEFAULT (dark grey) pass. The
  // label still differentiates the two neutral states. APPROVED is legacy — the
  // current select → notify → grant flow has no per-winner approve step.
  const STATUS_LABELS: Record<PrizeDrawEntryWinnerStatus, string> = {
    [PrizeDrawEntryWinnerStatus.SELECTED]: "Selected",
    [PrizeDrawEntryWinnerStatus.APPROVED]: "Approved",
    [PrizeDrawEntryWinnerStatus.REJECTED]: "Rejected",
    [PrizeDrawEntryWinnerStatus.REWARDED]: "Rewarded",
  }

  const STATUS_CHIP_TYPES: Record<PrizeDrawEntryWinnerStatus, SoChipType> = {
    [PrizeDrawEntryWinnerStatus.SELECTED]: SoChipType.DEFAULT,
    [PrizeDrawEntryWinnerStatus.APPROVED]: SoChipType.DEFAULT,
    [PrizeDrawEntryWinnerStatus.REJECTED]: SoChipType.ERROR,
    [PrizeDrawEntryWinnerStatus.REWARDED]: SoChipType.DEFAULT,
  }

  const headers: SoDataTableHeader[] = [
    { key: "prizeName", title: "Prize" },
    { key: "customerName", title: "Customer" },
    { key: "accountNumber", title: "Account" },
    { key: "county", title: "County" },
    { key: "previousWin", title: "Previous win" },
    { key: "anonymous", title: "Anonymous" },
    { key: "status", title: "Status" },
  ]

  type WinnerRow = {
    id: string
    prizeName: string
    customerName: string
    accountNumber: string
    county: string
    previousWin: string
    anonymous: boolean
    status: PrizeDrawEntryWinnerStatus
    statusLabel: string
    statusChipType: SoChipType
    flags: string[]
    rewardProcessError: string | null
    canReject: boolean
  }

  export default defineComponent({
    components: {
      SoAlert,
      SoButton,
      SoChip,
      SoDataTable,
      SoTextField,
      SoToggle,
      SoProgress,
      SoWidget,
      SoExportCsvButton,
      OpsWinnerReviewModal,
      OpsWinnerRejectModal,
    },
    props: {
      winners: {
        type: Array as PropType<IDrawWinner[]>,
        required: true,
      },
      rewards: {
        type: Array as PropType<IDrawReward[]>,
        default: () => [],
      },
      // Keyed by billingAccountId; accounts still loading or that failed are absent.
      directDebitStatuses: {
        type: Object as PropType<Record<string, boolean>>,
        default: () => ({}),
      },
      drawId: {
        type: String,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      error: {
        type: Boolean,
        default: false,
      },
      // While the BE is selecting winners (status CALCULATING_WINNERS) the table
      // is locked and replaced by an in-progress indicator (WS.21).
      calculating: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["saved"],
    data() {
      return {
        headers,
        RouteNames,
        // Snapshot populated only on Export click — a live list would re-download on every edit.
        csvItems: [] as WinnerRow[],
        // A local, editable copy of the winners. County / anonymity edits stage
        // here and are only persisted on Review & Save — never on blur / toggle.
        draftWinners: [] as IDrawWinner[],
        showReviewModal: false,
        showRejectModal: false,
        rejectWinner: null as IDrawWinner | null,
      }
    },
    computed: {
      items(): WinnerRow[] {
        return (
          this.draftWinners
            .map((winner) => ({
              id: winner.id,
              prizeName: this.prizeName(winner.prizeDrawRewardId),
              customerName: winner.surname,
              accountNumber: winner.accountNumber,
              county: winner.county,
              previousWin: winner.lastWonDate
                ? formatDateSlashed(winner.lastWonDate)
                : "—",
              anonymous: winner.nameVisibilityOptOut,
              status: winner.status,
              // BE has no failed status, so derive it from rewardProcessError.
              statusLabel: winner.rewardProcessError
                ? "Reward failed"
                : STATUS_LABELS[winner.status],
              statusChipType: winner.rewardProcessError
                ? SoChipType.ERROR
                : STATUS_CHIP_TYPES[winner.status],
              flags: [
                ...(winner.lastWonDate ? ["Previously won"] : []),
                // Missing means unknown, so only an explicit false is flagged.
                ...(this.directDebitStatuses[winner.billingAccountId] === false
                  ? ["No active Direct Debit"]
                  : []),
              ],
              rewardProcessError: winner.rewardProcessError,
              canReject: winner.status === PrizeDrawEntryWinnerStatus.SELECTED,
            }))
            // Rejected winners sort to the bottom; stable sort keeps the rest as-is.
            .sort(
              (a, b) =>
                Number(a.status === PrizeDrawEntryWinnerStatus.REJECTED) -
                Number(b.status === PrizeDrawEntryWinnerStatus.REJECTED),
            )
        )
      },
      csvData(): SoExportCsvButtonData {
        return { isLoading: false, items: this.csvItems }
      },
      changes(): IWinnerChange[] {
        return this.draftWinners.flatMap((draft) => {
          const original = this.winners.find((w) => w.id === draft.id)
          if (!original) return []
          const countyChanged = draft.county !== original.county
          const anonChanged =
            draft.nameVisibilityOptOut !== original.nameVisibilityOptOut
          if (!countyChanged && !anonChanged) return []
          return [
            {
              id: draft.id,
              surname: original.surname,
              fromCounty: original.county,
              toCounty: draft.county,
              countyChanged,
              fromAnon: original.nameVisibilityOptOut,
              toAnon: draft.nameVisibilityOptOut,
              anonChanged,
            },
          ]
        })
      },
      hasUnsavedChanges(): boolean {
        return this.changes.length > 0
      },
      summaryText(): string {
        const count = (status: PrizeDrawEntryWinnerStatus) =>
          this.winners.filter((winner) => winner.status === status).length
        // List only the statuses present so the parts always reconcile with the
        // total (an APPROVED winner would otherwise be missing from the breakdown).
        const parts = (
          [
            [PrizeDrawEntryWinnerStatus.SELECTED, "selected"],
            [PrizeDrawEntryWinnerStatus.APPROVED, "approved"],
            [PrizeDrawEntryWinnerStatus.REJECTED, "rejected"],
            [PrizeDrawEntryWinnerStatus.REWARDED, "rewarded"],
          ] as const
        )
          .map(([status, label]) => ({ n: count(status), label }))
          .filter(({ n }) => n > 0)
          .map(({ n, label }) => `${n} ${label}`)
        return `${parts.join(" · ")} (${this.winners.length} total)`
      },
      missingCountyCount(): number {
        return this.winners.filter((w) => {
          const c = (w.county ?? "").trim()
          return c === "" || c === "-"
        }).length
      },
      missingCountyTitle(): string {
        const n = this.missingCountyCount
        return `${n} winner${n === 1 ? " has" : "s have"} no county set`
      },
      // Rejected winners are already dealt with, so they don't count.
      noDirectDebitCount(): number {
        return this.winners.filter(
          (w) =>
            w.status !== PrizeDrawEntryWinnerStatus.REJECTED &&
            this.directDebitStatuses[w.billingAccountId] === false,
        ).length
      },
      noDirectDebitTitle(): string {
        const n = this.noDirectDebitCount
        return `${n} winner${n === 1 ? " has" : "s have"} no active Direct Debit`
      },
    },
    watch: {
      // Reset the draft whenever the persisted winners change (initial load and
      // after a save / reject refetch), discarding any stale local edits.
      winners: {
        immediate: true,
        handler(value: IDrawWinner[]) {
          this.draftWinners = cloneDeep(value)
        },
      },
    },
    methods: {
      prizeName(rewardId: string | null): string {
        if (!rewardId) return "—"
        return (
          this.rewards.find((reward) => reward.id === rewardId)?.name ?? "—"
        )
      },
      findDraft(id: string): IDrawWinner | undefined {
        return this.draftWinners.find((winner) => winner.id === id)
      },
      setCounty(id: string, value: string) {
        const draft = this.findDraft(id)
        if (draft) draft.county = value
      },
      setAnonymous(id: string, value: boolean) {
        const draft = this.findDraft(id)
        if (draft) draft.nameVisibilityOptOut = Boolean(value)
      },
      resetChanges() {
        this.draftWinners = cloneDeep(this.winners)
      },
      exportCsv() {
        // Export every winner, not just the page/filter currently shown in the table.
        this.csvItems = [...this.items]
      },
      openRejectModal(id: string) {
        this.rejectWinner = this.winners.find((w) => w.id === id) ?? null
        this.showRejectModal = true
      },
      onSaved() {
        // Parent refetches the winners; the watch above then resets the draft.
        this.$emit("saved")
      },
    },
  })
</script>

<style scoped>
  .winner-county-field {
    min-width: 160px;
  }
</style>
